import React, {FunctionComponent, useState, useEffect} from 'react'
import {
  Button,
  DataTable,
  DataTableSkeleton,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Pagination,
} from 'carbon-components-react'

import {useLocation, useHistory} from 'react-router-dom'
// @ts-ignore
import _isEqual from 'lodash.isequal'
import queryString from 'query-string'
import {api} from '../../../../../services'
import {Download} from '../../../../../components/Download'
import {Result as Item} from '../../../../../services/api/types'
import {FilterPanel} from '../FilterPanel'
import {Filters} from '../Filters'
import {FilterProps} from '../Filters/types'
import ResultCell, {resultsToDataRow} from '../ResultCell/ResultCell'
import {DEFAULT_DEVICE_ID, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DEFAULT_PROJECT_ID, CACHE_TTL_MINUTES} from '../Filters/content'
import {headerType, TableProps} from './types'

const PAGE_SIZES: [10, 25, 50, 100, 250, 500, 1000] = [10, 25, 50, 100, 250, 500, 1000]

interface ResultSummary {
  totalLoaded: number
  byStatus: Record<string, number>
  totalCpuHours: number
  totalElapsedHours: number
  totalClaimedCredit: number
  totalGrantedCredit: number
}

const computeSummary = (results: Item[]): ResultSummary => {
  const byStatus: Record<string, number> = {}
  let totalCpuHours = 0
  let totalElapsedHours = 0
  let totalClaimedCredit = 0
  let totalGrantedCredit = 0

  results.forEach((r) => {
    const name = r.statusName || 'Unknown'
    byStatus[name] = (byStatus[name] || 0) + 1
    totalCpuHours += r.cpuHours || 0
    totalElapsedHours += r.elapsedTime || 0
    totalClaimedCredit += r.claimedCredit || 0
    totalGrantedCredit += r.grantedCredit || 0
  })

  return {
    totalLoaded: results.length,
    byStatus,
    totalCpuHours,
    totalElapsedHours,
    totalClaimedCredit,
    totalGrantedCredit,
  }
}

const formatHours = (hours: number): string => {
  if (hours < 1) return `${(hours * 60).toFixed(1)} min`
  if (hours < 24) return `${hours.toFixed(1)} hrs`
  return `${(hours / 24).toFixed(1)} days`
}

const dataHeaders: headerType[] = [
  {
    key: 'resultName',
    header: 'Result name',
  },
  {
    key: 'deviceName',
    header: 'Device name',
  },
  {
    key: 'status',
    header: 'Status',
  },
  {
    key: 'sentTime',
    header: 'Sent time',
  },
  {
    key: 'dueTime',
    header: 'Time due',
  },
  {
    key: 'returnedTime',
    header: 'Return time',
  },
  {
    key: 'cpuHours',
    header: 'Cpu time/ Elapsed time',
  },
  {
    key: 'claimedCredit',
    header: 'Claimed credit/ Granted credit',
  },
]

const toArray = (param: any): string[] => {
  if (param === undefined) {
    return []
  }
  if (Array.isArray(param)) {
    return param
  }
  if (typeof param === 'string') {
    return [param]
  }
  return []
}

const toString = (param: any, defaultValue: string): string => {
  if (typeof param === 'string') {
    return param
  }
  return defaultValue
}

const toNumber = (param: any, defaultValue: number): number => {
  if (!isNaN(param)) {
    return parseInt(param, 10)
  }
  return defaultValue
}

// Status / outcome / validation filters are sent to `/ms/results` (AND across dimensions in WorkData).

// eslint-disable-next-line max-lines-per-function, complexity
const ResultsTable: FunctionComponent = () => {
  const history = useHistory()
  const location = useLocation()
  const queryParameters = queryString.parse(location.search)
  const [serverStatusIds, setServerStatusIds] = useState<string[]>(toArray(queryParameters.serverStatusIds))
  const [outcomeIds, setOutcomeIds] = useState<string[]>(toArray(queryParameters.outcomeIds))
  const [validationStatusIds, setValidationStatusIds] = useState<string[]>(toArray(queryParameters.validationStatusIds))
  const [deviceId, setDeviceId] = useState<string>(toString(queryParameters.deviceId, DEFAULT_DEVICE_ID))
  const [projectId, setProjectId] = useState<string>(toString(queryParameters.projectId, DEFAULT_PROJECT_ID))
  const [sortField, setSortField] = useState<string>(toString(queryParameters.sortBy, ''))
  const [sortDirection, setSortDirection] = useState<string>(toString(queryParameters.sortDirection, ''))
  const [searchValue, setSearchValue] = useState<string>()

  const [allResults, setAllResults] = useState<Item[] | undefined>(undefined)
  const [displayResults, setDisplayResults] = useState<Item[]>([])
  const [currentPageResults, setCurrentPageResults] = useState<Item[]>([])

  const [maxRecordsAvailable, setMaxRecordsAvailable] = useState<number>(0)
  const [totalServerRecords, setTotalServerRecords] = useState<number>(0)
  const [currentPageSize, setCurrentPageSize] = useState<number>(toNumber(queryParameters.limit, DEFAULT_PAGE_SIZE))
  const [currentPage, setCurrentPage] = useState<number>(toNumber(queryParameters.pageNumber, DEFAULT_PAGE_NUMBER))
  const [fetchedPageCount, setFetchedPageCount] = useState<number>(0)

  const [dataRows, setDataRows] = useState<any>([])
  const [fetchingAll, setFetchingAll] = useState<boolean>(false)
  const [fetchedAll, setFetchedAll] = useState<boolean>(false)
  const [showSummary, setShowSummary] = useState<boolean>(false)
  const [summary, setSummary] = useState<ResultSummary | null>(null)

  const updateTable = (item: FilterProps) => {
    setDeviceId(item.deviceId)
    setProjectId(item.projectId)
    setServerStatusIds(item.serverStatusIds)
    setValidationStatusIds(item.validationStatusIds)
    setOutcomeIds(item.outcomeIds)
    // Reset to page 1 when filters change (filtered result set changes)
    setCurrentPage(DEFAULT_PAGE_NUMBER)
  }

  const download = () => {
    api.getResultsForDownload(deviceId, projectId, serverStatusIds, outcomeIds, validationStatusIds)
  }

  const fetchAllResults = async () => {
    setFetchingAll(true)
    const sortBy = sortField || 'sentTime'
    const resp = await api.getResults(deviceId, projectId, serverStatusIds, outcomeIds, validationStatusIds, sortBy, 1, 0)
    if (resp && resp.data) {
      const items = resp.data.items || []
      setAllResults(items)
      setFetchedAll(true)
      if (resp.data.metadata && resp.data.metadata.recordsAvailable !== undefined) {
        setTotalServerRecords(resp.data.metadata.recordsAvailable)
      }
    }
    setFetchingAll(false)
    setCurrentPage(DEFAULT_PAGE_NUMBER)
  }

  const handlePageChange = (data: {page: number; pageSize: number}) => {
    setCurrentPage(data.page)
    setCurrentPageSize(data.pageSize)
  }

  const customSearch = (inputValue: any) => {
    if (inputValue && inputValue.currentTarget && inputValue.currentTarget.value) {
      setSearchValue(inputValue.currentTarget.value)
    } else {
      setSearchValue('')
    }
  }

  const handleHeaderClick = (e: any) => {
    const headerText = e.target.innerText
    const header = dataHeaders.find((header) => header.header === headerText)
    if (header === undefined) {
      return
    }

    setCurrentPage(DEFAULT_PAGE_NUMBER)
    if (sortField === header.key) {
      if (sortDirection === '') {
        setSortDirection('ASC')
      } else if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortField('')
        setSortDirection('')
      }
    } else {
      setSortField(header.key)
      setSortDirection('ASC')
    }
  }

  useEffect(() => {
    const queryParameters = queryString.parse(location.search)

    const tmpDeviceId = toString(queryParameters.deviceId, DEFAULT_DEVICE_ID)
    const tmpProjectId = toString(queryParameters.projectId, DEFAULT_PROJECT_ID)
    const tmpServerStatusIds = toArray(queryParameters.serverStatusIds)
    const tmpValidationStatusIds = toArray(queryParameters.validationStatusIds)
    const tmpOutcomeIds = toArray(queryParameters.outcomeIds)
    const tmpCurrentPage = toNumber(queryParameters.pageNumber, DEFAULT_PAGE_NUMBER)
    const tmpCurrentPageSize = toNumber(queryParameters.limit, DEFAULT_PAGE_SIZE)

    if (tmpDeviceId !== deviceId) {
      setDeviceId(tmpDeviceId)
    }
    if (tmpProjectId !== projectId) {
      setProjectId(tmpProjectId)
    }
    if (!_isEqual(tmpServerStatusIds, serverStatusIds)) {
      setServerStatusIds(tmpServerStatusIds)
    }
    if (!_isEqual(tmpValidationStatusIds, validationStatusIds)) {
      setValidationStatusIds(tmpValidationStatusIds)
    }
    if (!_isEqual(tmpOutcomeIds, outcomeIds)) {
      setOutcomeIds(tmpOutcomeIds)
    }
    if (tmpCurrentPage !== currentPage) {
      setCurrentPage(tmpCurrentPage)
    }
    if (tmpCurrentPageSize !== currentPageSize) {
      setCurrentPageSize(tmpCurrentPageSize)
    }
  }, [location])

  // Initial fetch and re-fetch when device/project/status filters change
  useEffect(() => {
    const getData = async () => {
      setAllResults(undefined)
      setFetchedPageCount(0)
      setFetchedAll(false)
      const sortBy = sortField || 'sentTime'
      const resp = await api.getResults(deviceId, projectId, serverStatusIds, outcomeIds, validationStatusIds, sortBy, 1, DEFAULT_PAGE_SIZE)
      if (resp && resp.data) {
        const items = resp.data.items || []
        setAllResults(items)
        setFetchedPageCount(1)
        if (resp.data.metadata && resp.data.metadata.recordsAvailable !== undefined) {
          setTotalServerRecords(resp.data.metadata.recordsAvailable)
        } else {
          setTotalServerRecords(items.length)
        }
      }
    }
    getData()
    setCurrentPage(DEFAULT_PAGE_NUMBER)
  }, [deviceId, projectId, serverStatusIds, outcomeIds, validationStatusIds])

  // Fetch more results from the server when paging beyond loaded data
  useEffect(() => {
    if (!allResults || fetchedAll) {
      return
    }
    const neededIndex = currentPage * currentPageSize
    const haveEnough = neededIndex <= allResults.length || allResults.length >= totalServerRecords
    if (!haveEnough) {
      const fetchMore = async () => {
        const nextServerPage = fetchedPageCount + 1
        const sortBy = sortField || 'sentTime'
        const resp = await api.getResults(deviceId, projectId, serverStatusIds, outcomeIds, validationStatusIds, sortBy, nextServerPage, DEFAULT_PAGE_SIZE)
        if (resp && resp.data && resp.data.items) {
          setAllResults((prev) => (prev ? [...prev, ...resp.data.items] : resp.data.items))
          setFetchedPageCount(nextServerPage)
          if (resp.data.metadata && resp.data.metadata.recordsAvailable !== undefined) {
            setTotalServerRecords(resp.data.metadata.recordsAvailable)
          }
        }
      }
      fetchMore()
    }
  }, [
    currentPage,
    currentPageSize,
    allResults,
    totalServerRecords,
    fetchedPageCount,
    fetchedAll,
    deviceId,
    projectId,
    serverStatusIds,
    outcomeIds,
    validationStatusIds,
  ])

  // Update URL query string to reflect current state
  useEffect(() => {
    const queryString = api.getParamsForResults(
      deviceId,
      projectId,
      serverStatusIds,
      outcomeIds,
      validationStatusIds,
      sortField,
      currentPage,
      currentPageSize,
      sortDirection,
    )
    let tmpSearch = location.search
    if (tmpSearch.length > 0 && tmpSearch[0] === '?') {
      tmpSearch = tmpSearch.substr(1)
    }
    if (queryString !== tmpSearch) {
      history.push({pathname: location.pathname, search: queryString})
    }
  }, [deviceId, projectId, serverStatusIds, outcomeIds, validationStatusIds, sortField, currentPage, currentPageSize, sortDirection])

  // Client-side search and sort on the server-filtered result set
  useEffect(() => {
    if (allResults) {
      let tmpResults = allResults

      if (searchValue && searchValue !== '') {
        const q = searchValue.toLowerCase()
        tmpResults = tmpResults.filter((item: Item) => item.resultName.toLowerCase().includes(q))
      }

      if (sortField) {
        tmpResults = [...tmpResults]
        tmpResults.sort((a, b) => {
          const direction = sortDirection === 'DESC' ? -1 : 1
          if (a[sortField] === b[sortField]) {
            return 0
          }
          if (a[sortField] === undefined) {
            return -1 * direction
          } else if (b[sortField] === undefined) {
            return 1 * direction
          } else if (a[sortField] > b[sortField]) {
            return 1 * direction
          }
          return -1 * direction
        })
      }

      setDisplayResults(tmpResults)

      // Text search is client-only: total row count is unknown until everything is loaded.
      const hasTextSearch = !!(searchValue && searchValue !== '')
      if (hasTextSearch) {
        setMaxRecordsAvailable(tmpResults.length)
      } else {
        setMaxRecordsAvailable(totalServerRecords)
      }
    }
  }, [allResults, searchValue, sortField, sortDirection, totalServerRecords])

  // Recompute summary stats when displayed (filtered) results change
  useEffect(() => {
    if (displayResults && displayResults.length > 0) {
      setSummary(computeSummary(displayResults))
    } else {
      setSummary(null)
    }
  }, [displayResults])

  // Slice the current page from display results (client-side paging)
  useEffect(() => {
    const tmpResults = displayResults.slice((currentPage - 1) * currentPageSize, currentPageSize * currentPage)
    setCurrentPageResults(tmpResults)
    setDataRows(tmpResults.map(resultsToDataRow))
  }, [displayResults, currentPage, currentPageSize])

  if (allResults === undefined) {
    return <DataTableSkeleton />
  }

  return (
    <>
      <div style={{marginBottom: '1rem'}}>
        {!fetchedAll && totalServerRecords > DEFAULT_PAGE_SIZE && (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            title={`Showing the most recent ${
              allResults ? allResults.length.toLocaleString() : DEFAULT_PAGE_SIZE
            } of ${totalServerRecords.toLocaleString()} results.`}
            subtitle={`Paging beyond ${DEFAULT_PAGE_SIZE} results, or using the "Fetch All My Results" button, will allow you to sort, search, and filter more than the most recent ${DEFAULT_PAGE_SIZE} results.`}
          />
        )}
        {fetchedAll && (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            title={`All ${totalServerRecords.toLocaleString()} of your results loaded.`}
            subtitle={`Recent activity will not be reflected until your cached results expire. You may refresh your results manually after ${CACHE_TTL_MINUTES}m.`}
          />
        )}
        <div style={{marginTop: '0.5rem', display: 'flex', gap: '0.5rem'}}>
          {!fetchedAll && totalServerRecords > DEFAULT_PAGE_SIZE && (
            <Button kind="tertiary" size="small" disabled={fetchingAll} onClick={fetchAllResults}>
              {fetchingAll ? 'Fetching all results...' : 'Fetch All My Results'}
            </Button>
          )}
          {allResults && allResults.length > 0 && (
            <Button kind="ghost" size="small" onClick={() => setShowSummary(!showSummary)}>
              {showSummary ? 'Hide Summary' : 'Show Summary'}
            </Button>
          )}
        </div>
        {showSummary && summary && (
          <div style={{marginTop: '0.75rem', padding: '1rem', backgroundColor: '#f4f4f4', borderRadius: '4px'}}>
            <h5 style={{marginBottom: '0.5rem'}}>
              {(() => {
                const n = summary.totalLoaded
                const loaded = allResults?.length ?? 0
                const total = Math.max(totalServerRecords, loaded, n)
                const hasSf = serverStatusIds.length > 0 || outcomeIds.length > 0 || validationStatusIds.length > 0
                const hasSx = !!(searchValue && searchValue !== '')
                if (hasSx) {
                  return `Summary for ${n.toLocaleString()} result(s) matching search (among ${loaded.toLocaleString()} loaded).`
                }
                if (hasSf) {
                  return `Summary for ${n.toLocaleString()} result(s) matching your filters (${total.toLocaleString()} total in this view).`
                }
                if (fetchedAll || loaded >= totalServerRecords) {
                  return `Summary for ${n.toLocaleString()} result(s).`
                }
                return `Summary for ${n.toLocaleString()} of ${loaded.toLocaleString()} loaded (${total.toLocaleString()} total available).`
              })()}
            </h5>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem'}}>
              <div>
                <strong>By Status:</strong>
                <ul style={{listStyle: 'none', padding: 0, margin: '0.25rem 0'}}>
                  {Object.entries(summary.byStatus)
                    .sort(([, a], [, b]) => b - a)
                    .map(([status, count]) => (
                      <li key={status}>
                        {status}: {count.toLocaleString()}
                      </li>
                    ))}
                </ul>
              </div>
              <div>
                <strong>Compute Time:</strong>
                <ul style={{listStyle: 'none', padding: 0, margin: '0.25rem 0'}}>
                  <li>CPU time: {formatHours(summary.totalCpuHours)}</li>
                  <li>Elapsed time: {formatHours(summary.totalElapsedHours)}</li>
                </ul>
              </div>
              <div>
                <strong>Credit:</strong>
                <ul style={{listStyle: 'none', padding: 0, margin: '0.25rem 0'}}>
                  <li>Claimed: {summary.totalClaimedCredit.toLocaleString()}</li>
                  <li>Granted: {summary.totalGrantedCredit.toLocaleString()}</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
      <DataTable rows={dataRows} headers={dataHeaders} isSortable>
        {/* @ts-ignore */}
        {({rows, headers, getHeaderProps, getRowProps, getTableProps, getToolbarProps, getTableContainerProps}: TableProps) => (
          <TableContainer {...getTableContainerProps()}>
            <TableToolbar {...getToolbarProps()} aria-label="data table toolbar">
              <TableToolbarContent>
                <TableToolbarSearch onChange={customSearch} value={searchValue} placeholder="Search result name" />
                <FilterPanel>
                  <Filters
                    updateTable={updateTable}
                    deviceId={deviceId}
                    projectId={projectId}
                    serverStatusIds={serverStatusIds}
                    outcomeIds={outcomeIds}
                    validationStatusIds={validationStatusIds}
                  />
                </FilterPanel>
                <Download download={download} label="Download results" />
              </TableToolbarContent>
            </TableToolbar>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow onClick={(e) => handleHeaderClick(e)}>
                  {headers.map((header: headerType) => (
                    <TableHeader key={header.key} {...getHeaderProps({header})}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row: {id: string; cells: any[]}) => (
                  <TableRow key={row.id} {...getRowProps({row})}>
                    {row.cells.map((cell) => (
                      <TableCell className="workunit-allow-wrap" key={cell.id}>
                        <ResultCell rowId={row.id} cell={cell} results={currentPageResults} isWorkunitTable={false} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>
      <div className="pagination-table">
        <Pagination pageSizes={PAGE_SIZES} pageSize={currentPageSize} page={currentPage} totalItems={maxRecordsAvailable || 0} onChange={handlePageChange} />
      </div>
    </>
  )
}

export default ResultsTable
